/**
 * Agent "My Clients" roster.
 *
 * Before this page existed (sprint audit gap) an agent had no way to
 * browse their roster cold — every existing roster surface (deposits,
 * commitments, withdrawals) is filtered by a transactional state, so
 * a client with no recent activity was effectively invisible. Search
 * worked but only if the agent remembered the name.
 *
 * Scope: read-only list with client-side filter and email/profile deep-
 * links. No state mutation. Reuses GET /api/v1/agents/{id}/clients
 * (already in agent.service) — no new backend endpoint.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import agentService from '../../services/agent.service';
import type { ClientSummary } from '../../services/manager.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { fullName } from '../../lib/fullName';
import { colors } from '../../styles/theme';

export default function MyClients(): JSX.Element {
  const [clients, setClients] = useState<ClientSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    agentService
      .listMyClients()
      .then((items) => {
        if (!cancelled) setClients(items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(getApiErrorMessage(err, 'Could not load your clients.'));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!clients) return [];
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => {
      const full = `${c.first_name} ${c.last_name}`.toLowerCase();
      return full.includes(q) || c.email.toLowerCase().includes(q);
    });
  }, [clients, query]);

  const totalCount = clients?.length ?? 0;
  const clientNoun = totalCount === 1 ? 'client' : 'clients';
  const countSummary = query
    ? `${filtered.length} of ${totalCount} match`
    : `${totalCount} ${clientNoun}`;

  return (
    <>
      <PageHeader
        title="My Clients"
        subtitle="Every active client assigned to you. Open a profile to see balances, cases, and the call guide."
      />

      {loadError && (
        <div
          className="rounded-lg p-4 mb-6 text-sm"
          style={{
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
          }}
          role="alert"
        >
          {loadError}
        </div>
      )}

      <div className="mb-4">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full max-w-md px-3 py-2 rounded-lg text-sm"
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            color: colors.ink,
          }}
        />
      </div>

      {clients === null && !loadError && (
        <div
          className="rounded-2xl p-6"
          style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <p style={{ color: colors.muted }}>Loading…</p>
        </div>
      )}

      {clients !== null && clients.length === 0 && (
        <div
          className="rounded-2xl p-6"
          style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <p style={{ color: colors.muted }}>
            You have no clients assigned to you yet. A manager assigns clients
            via the manager dashboard.
          </p>
        </div>
      )}

      {clients !== null && clients.length > 0 && filtered.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>
          No clients match &ldquo;{query}&rdquo;.
        </p>
      )}

      {filtered.length > 0 && (
        <ul className="space-y-2">
          {filtered.map((c) => {
            const name = fullName({ first_name: c.first_name, last_name: c.last_name });
            return (
              <li
                key={c.id}
                className="rounded-xl p-4 flex items-center justify-between gap-3"
                style={{
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate" style={{ color: colors.ink }}>
                    {name}
                  </p>
                  <a
                    href={`mailto:${c.email}`}
                    className="text-xs font-mono truncate block"
                    style={{ color: colors.muted }}
                  >
                    {c.email}
                  </a>
                </div>
                <Link
                  to={`/agent/clients/${c.id}`}
                  className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold"
                  style={{ background: colors.navy, color: '#FFFFFF' }}
                >
                  Open profile →
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {clients !== null && filtered.length > 0 && (
        <p
          className="text-xs mt-4"
          style={{ color: colors.mutedSoft }}
          aria-live="polite"
        >
          {countSummary}
        </p>
      )}
    </>
  );
}
